"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState, useTransition } from "react";
import { Check, ChevronDown, FlaskConical, Route, ShieldCheck, Sparkles, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  demoPersonas,
  previewRoutes,
  roleDescriptions,
  roleLabels,
  type AppRole,
  type SessionContext,
} from "@/lib/phaseone";

export function PreviewOverlay({ context }: { context: SessionContext | null }) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);
  const [pending, startTransition] = useTransition();
  const currentRole = context?.role ?? null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const currentRoute = useMemo(
    () => previewRoutes.flatMap((group) => group.routes).find((route) => route.href === pathname),
    [pathname],
  );

  async function switchRole(role: AppRole) {
    startTransition(async () => {
      const response = await fetch("/api/preview/session", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ role }),
      });
      if (response.ok) {
        router.refresh();
      }
    });
  }

  function setState(state: string) {
    const params = new URLSearchParams(searchParams.toString());
    if (state === "populated") params.delete("previewState");
    else params.set("previewState", state);
    router.replace(`${pathname}${params.size ? `?${params}` : ""}`);
  }

  if (process.env.NEXT_PUBLIC_PREVIEW_MODE !== "true") return null;

  return (
    <div className="fixed right-3 bottom-3 z-[100] sm:right-5 sm:bottom-5" data-testid="preview-overlay">
      {open && (
        <div className="mb-2 w-[min(23rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-white/10 bg-[#10231f]/97 text-[#f5f0e6] shadow-2xl shadow-black/35 backdrop-blur-xl">
          <div className="flex items-start justify-between gap-4 border-b border-white/10 px-4 py-3.5">
            <div>
              <div className="flex items-center gap-2">
                <FlaskConical className="text-[#ff7a38]" aria-hidden="true" />
                <p className="font-heading text-base font-semibold">Preview control</p>
                <Badge className="border-[#ff7a38]/30 bg-[#ff7a38]/12 text-[#ffb089]">Synthetic</Badge>
              </div>
              <p className="mt-1 text-[0.68rem] tracking-[0.12em] text-white/50 uppercase">Role × route × state</p>
            </div>
            <Button variant="ghost" size="icon-sm" className="text-white/70 hover:bg-white/10 hover:text-white" onClick={() => setOpen(false)} aria-label="Close preview controls">
              <X aria-hidden="true" />
            </Button>
          </div>

          <ScrollArea className="h-[min(72vh,42rem)]">
            <div className="p-4">
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold">{context?.fullName ?? "No active persona"}</p>
                    <p className="mt-0.5 text-[0.68rem] text-white/50">{currentRole ? roleLabels[currentRole] : "Public session"}</p>
                  </div>
                  <ShieldCheck className="text-emerald-300" aria-hidden="true" />
                </div>
                <p className="mt-2 text-[0.68rem] leading-5 text-white/55">
                  {currentRole ? roleDescriptions[currentRole] : "Choose a persona to enter the protected product."}
                </p>
              </div>

              <p className="mt-4 mb-2 text-[0.65rem] font-semibold tracking-[0.14em] text-white/45 uppercase">Persona</p>
              <div className="grid grid-cols-2 gap-1.5">
                {(Object.keys(roleLabels) as AppRole[]).map((role) => (
                  <Button
                    key={role}
                    variant="ghost"
                    disabled={pending}
                    onClick={() => switchRole(role)}
                    className={cn(
                      "h-auto min-h-14 justify-start px-2.5 py-2 text-left text-white/75 hover:bg-white/10 hover:text-white",
                      currentRole === role && "bg-white/12 text-white ring-1 ring-[#ff7a38]/40",
                    )}
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 text-xs font-semibold">
                        {currentRole === role && <Check className="text-[#ff7a38]" aria-hidden="true" />}
                        {roleLabels[role]}
                      </span>
                      <span className="mt-0.5 block truncate text-[0.64rem] font-normal text-white/45">{demoPersonas[role].name}</span>
                    </span>
                  </Button>
                ))}
              </div>

              <Separator className="my-4 bg-white/10" />
              <p className="mb-2 text-[0.65rem] font-semibold tracking-[0.14em] text-white/45 uppercase">Data state</p>
              <div className="grid grid-cols-3 gap-1.5">
                {["populated", "empty", "blocked"].map((state) => {
                  const active = (searchParams.get("previewState") ?? "populated") === state;
                  return (
                    <Button key={state} variant="ghost" size="sm" onClick={() => setState(state)} className={cn("text-[0.67rem] text-white/65 hover:bg-white/10 hover:text-white", active && "bg-white/12 text-white")}>{state}</Button>
                  );
                })}
              </div>

              <Separator className="my-4 bg-white/10" />
              <div className="flex items-center justify-between gap-3">
                <p className="text-[0.65rem] font-semibold tracking-[0.14em] text-white/45 uppercase">All paths</p>
                <span className="text-[0.62rem] text-white/35">{previewRoutes.reduce((total, group) => total + group.routes.length, 0)} routes</span>
              </div>

              <div className="mt-2 grid gap-3">
                {previewRoutes.map((group) => (
                  <div key={group.group}>
                    <p className="mb-1.5 text-[0.64rem] font-medium text-[#ffb089]">{group.group}</p>
                    <div className="grid gap-0.5">
                      {group.routes.map((route) => {
                        const active = pathname === route.href;
                        const authorized = route.roles === "public" || (currentRole ? route.roles.includes(currentRole) : false);
                        return (
                          <Link
                            key={route.href}
                            href={route.href}
                            onClick={() => setOpen(false)}
                            className={cn(
                              "flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-xs text-white/65 transition-colors hover:bg-white/8 hover:text-white",
                              active && "bg-white/10 text-white",
                            )}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <span className={cn("size-1.5 shrink-0 rounded-full bg-white/20", authorized ? "bg-emerald-300" : "bg-amber-300")} />
                              <span className="truncate">{route.label}</span>
                            </span>
                            <span className="shrink-0 font-mono text-[0.58rem] tracking-wide text-white/35 uppercase">{route.flow}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>

          <div className="flex items-center justify-between gap-3 border-t border-white/10 px-4 py-2.5 text-[0.62rem] text-white/45">
            <span className="flex items-center gap-1.5"><Route aria-hidden="true" /> {currentRoute?.flow ?? "Custom path"}</span>
            <span>⌘ K</span>
          </div>
        </div>
      )}

      <Button
        size="lg"
        onClick={() => setOpen((value) => !value)}
        className="h-11 rounded-full border border-white/10 bg-[#10231f] px-4 text-[#f5f0e6] shadow-xl shadow-black/25 hover:bg-[#17332d]"
        aria-expanded={open}
        aria-label="Toggle preview controls"
      >
        <Sparkles className="text-[#ff7a38]" aria-hidden="true" />
        <span>Preview</span>
        <ChevronDown className={cn("transition-transform", open && "rotate-180")} aria-hidden="true" />
      </Button>
    </div>
  );
}

